import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { IssueResolverType, MessageV2DTO, PartnerDTO } from 'support/types';
import { DisplayModes } from 'components/DataTable/Types';
import { AlertSection } from 'components/Display/Alert';
import { Translate } from 'components/Translate/Translate';

import { ContentBanner } from './ContentBanner';
import { ErrorsNavigation } from './ErrorsNavigation/ErrorsNavigation';
import { LoadingErrors } from './LoadingErrors';

type DatachecksErrorBannerProps = {
  mode: DisplayModes;
  issueResolver?: IssueResolverType;
  isCurrentPartnerReceiver?: boolean;
  senderPartner?: PartnerDTO | null;
  receiverPartner?: PartnerDTO | null;
  isLoadingDatachecksErrors?: boolean;
  blockingError?: MessageV2DTO['blocking_error'];
  numberOfErrors: number;
};

export const DatachecksErrorBanner = ({
  mode,
  issueResolver,
  isCurrentPartnerReceiver,
  senderPartner,
  receiverPartner,
  isLoadingDatachecksErrors,
  blockingError,
  numberOfErrors,
}: DatachecksErrorBannerProps) => {
  if (issueResolver === IssueResolverType.PROCUROS) {
    return <ProcurosDatachecksContent blockingError={blockingError} />;
  }

  if (issueResolver === IssueResolverType.OTHER_PARTNER) {
    return (
      <OtherPartnerDatachecksContent
        mode={mode}
        isCurrentPartnerReceiver={isCurrentPartnerReceiver}
        senderPartner={senderPartner}
        receiverPartner={receiverPartner}
        isLoadingDatachecksErrors={isLoadingDatachecksErrors}
        numberOfErrors={numberOfErrors}
      />
    );
  }

  return (
    <CurrentPartnerDatachecksContent
      mode={mode}
      isLoadingDatachecksErrors={isLoadingDatachecksErrors}
      blockingError={blockingError}
      numberOfErrors={numberOfErrors}
    />
  );
};

const ProcurosDatachecksContent = ({ blockingError }: { blockingError?: MessageV2DTO['blocking_error'] }) => {
  const { t, i18n } = useTranslation();

  const code = blockingError?.code;
  let title: string | null = blockingError?.title || null;
  let description: ReactNode = blockingError?.description ? <Translate i18nKey={blockingError.description} /> : null;
  let howToFix: ReactNode = blockingError?.howToFix ? <Translate i18nKey={blockingError.howToFix} /> : null;

  if (code) {
    const titleKey = `webedi:viewDocument.procurosIsIssueResolver.${code}.title`;
    const descriptionKey = `webedi:viewDocument.procurosIsIssueResolver.${code}.description`;
    const howToFixKey = `webedi:viewDocument.procurosIsIssueResolver.${code}.howToFix`;

    title = i18n.exists(titleKey) ? t(titleKey) : title;
    description = i18n.exists(descriptionKey) ? (
      <>
        <span>{t(descriptionKey)}</span>
        <br />
      </>
    ) : (
      description
    );
    howToFix = i18n.exists(howToFixKey) ? t(howToFixKey) : howToFix;
  }

  return (
    <ContentBanner severity="neutral">
      <AlertSection severity="neutral" title={title}>
        {description}
        {howToFix}
      </AlertSection>
    </ContentBanner>
  );
};

const OtherPartnerDatachecksContent = ({
  mode,
  isCurrentPartnerReceiver,
  senderPartner,
  receiverPartner,
  isLoadingDatachecksErrors,
  numberOfErrors,
}: {
  mode: DisplayModes;
  isCurrentPartnerReceiver?: boolean;
  senderPartner?: PartnerDTO | null;
  receiverPartner?: PartnerDTO | null;
  isLoadingDatachecksErrors?: boolean;
  numberOfErrors: number;
}) => {
  const { t } = useTranslation();

  const partnerRoleInMessage = isCurrentPartnerReceiver ? 'receiver' : 'sender';
  const receiverPartnerName = receiverPartner?.name || '';
  const senderPartnerName = senderPartner?.name || '';
  const issueResolverName = partnerRoleInMessage === 'receiver' ? senderPartnerName : receiverPartnerName;
  const datachecksErrorLoadingDescription = t('webedi:viewDocument.messageErrorBanner.loadingDatachecks.description');

  return (
    <ContentBanner severity="neutral">
      <AlertSection
        severity="neutral"
        title={t(`webedi:viewDocument.${partnerRoleInMessage}.noActionRequiredBanner.datachecksError.title`)}
      >
        {isLoadingDatachecksErrors
          ? datachecksErrorLoadingDescription
          : t(`webedi:viewDocument.${partnerRoleInMessage}.noActionRequiredBanner.datachecksError.description`, {
              count: numberOfErrors,
              receiverPartnerName,
              senderPartnerName,
              issueResolverName,
              numberOfFieldsWithErrors: numberOfErrors,
            })}
      </AlertSection>
      {isLoadingDatachecksErrors ? <LoadingErrors /> : <ErrorsNavigation mode={mode} numberOfErrors={numberOfErrors} />}
    </ContentBanner>
  );
};

const CurrentPartnerDatachecksContent = ({
  mode,
  isLoadingDatachecksErrors,
  blockingError,
  numberOfErrors,
}: {
  mode: DisplayModes;
  isLoadingDatachecksErrors?: boolean;
  blockingError?: MessageV2DTO['blocking_error'];
  numberOfErrors: number;
}) => {
  const { t } = useTranslation();

  if (numberOfErrors === 0 && !isLoadingDatachecksErrors) {
    return null;
  }

  const datachecksErrorLoadingDescription = t('webedi:viewDocument.messageErrorBanner.loadingDatachecks.description');

  return (
    <ContentBanner severity="error">
      <div>
        <p className="font-bold text-red-700">{blockingError?.description}</p>
        <p>
          {mode === DisplayModes.view
            ? isLoadingDatachecksErrors
              ? datachecksErrorLoadingDescription
              : t('webedi:viewDocument.errorBanner.fix')
            : t('webedi:editDocument.errorBanner.fix')}
        </p>
      </div>
      {isLoadingDatachecksErrors ? <LoadingErrors /> : <ErrorsNavigation mode={mode} numberOfErrors={numberOfErrors} />}
    </ContentBanner>
  );
};

/**
 * i18n keys
 * t('webedi:viewDocument.procurosIsIssueResolver.1724421292.title')
 * t('webedi:viewDocument.procurosIsIssueResolver.1724421292.description')
 * t('webedi:viewDocument.procurosIsIssueResolver.1724421292.howToFix')
 * t('webedi:viewDocument.messageErrorBanner.loadingDatachecks.description')
 * t('webedi:viewDocument.sender.noActionRequiredBanner.datachecksError.title')
 * t('webedi:viewDocument.sender.noActionRequiredBanner.datachecksError.description', { count })
 * t('webedi:viewDocument.receiver.noActionRequiredBanner.datachecksError.title')
 * t('webedi:viewDocument.receiver.noActionRequiredBanner.datachecksError.description', { count })
 * t('webedi:viewDocument.errorBanner.fix')
 * t('webedi:editDocument.errorBanner.fix')
 */
